import { defineStore } from "pinia";

import router from "@/router";
import jwtDecode from "jwt-decode";
import { CurrentUserModel } from "../modules/auth/models/current-user.model";
import appPaths from "../router/app.paths";

export interface DecodedToken {
  exp?: number;
  iat?: number;
  user?: CurrentUserModel;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    isLoggedIn: false,
    idToken: null as string | null,
    refreshToken: null as string | null,
    idTokenExpire: null as number | null,
    decodedToken: null,
    user: null as null | CurrentUserModel,
    returnUrl: null as string | null,
    lastTokenRefresh: null as number | null,
    refreshPromise: null as Promise<string> | null // Add this to track ongoing refresh
  }),

  persist: {
    enabled: true,
    strategies: [{ storage: localStorage }],
  },

  getters: {
    role: (state) => {
      return state.user?.role;
    }
  },

  actions: {
    setLoggedIn(payload: boolean) {
      this.isLoggedIn = payload;
    },
    setIdToken(idToken: string, expiresIn: number, refreshToken?: string) {
      this.idToken = idToken;
      this.decodedToken = jwtDecode<DecodedToken>(idToken);
      this.user = this.decodedToken.user;
      this.idTokenExpire = new Date().getTime() + (expiresIn * 1000);
      this.lastTokenRefresh = new Date().getTime();
      if (refreshToken) {
        this.refreshToken = refreshToken;
      }
    },
    async getIdToken(): Promise<string> {
      // Check if token is expired or will expire in the next 5 minutes
      const fiveMinutesFromNow = new Date().getTime() + (5 * 60 * 1000);
      
      // If token is still valid, return it
      if (this.idTokenExpire && fiveMinutesFromNow <= this.idTokenExpire) {
        return this.idToken;
      }
      
      // If there's already a refresh in progress, wait for it
      if (this.refreshPromise) {
        try {
          return await this.refreshPromise;
        } catch (error) {
          // If the ongoing refresh failed, we'll try again below
          this.refreshPromise = null;
        }
      }
      
      // Start token refresh
      if (this.refreshToken) {
        this.refreshPromise = this.performTokenRefresh();
        
        try {
          const newToken = await this.refreshPromise;
          this.refreshPromise = null;
          return newToken;
        } catch (error) {
          this.refreshPromise = null;
          // If refresh fails, logout the user
          this.logout();
          throw new Error('Token refresh failed');
        }
      } else {
        this.logout();
        throw new Error('No refresh token available');
      }
    },
    
    async performTokenRefresh(): Promise<string> {
      try {
        // Import refresh token service dynamically to avoid circular imports
        const { refreshToken: refreshTokenService } = await import("@/modules/auth/auth.service");
        
        const response = await refreshTokenService({ refreshToken: this.refreshToken });
        
        // Update tokens with new values
        this.setIdToken(response.data.idToken, response.data.expiresIn, response.data.refreshToken);
        
        return this.idToken;
      } catch (error) {
        console.error('Token refresh failed:', error);
        throw error;
      }
    },
    async logout() {
      // Clear any ongoing refresh promise
      this.refreshPromise = null;
      
      try {
        // Import logout service dynamically to avoid circular imports
        const { logout: logoutService } = await import("@/modules/auth/auth.service");
        
        if (this.refreshToken) {
          await logoutService({ refreshToken: this.refreshToken });
        }
      } catch (error) {
        console.error('Logout error:', error);
        // Continue with logout even if API call fails
      } finally {
        router.push({ path: appPaths.authLogin });
        this.$reset();
      }
    },
  },
});
